import * as React from 'react'
import axios, { CancelToken, CancelTokenSource } from 'axios'
import { Modal, Form } from 'antd'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import PayerForm from './PayerForm'
import { pipe } from 'fp-ts/lib/function'
import { addPayerAction, toggleCreatePayerModal } from './types'
import { option, either, task } from 'fp-ts'
import { Customer } from '../../types'
import { getPayerByEmailOrPhonenumber } from './api'
import { loadable, useOrganization } from '@library/react-toolkit'

export const checkExists = (
  checker: (
    _v: string,
    _tkn: CancelToken
  ) => ReturnType<typeof getPayerByEmailOrPhonenumber>
) => {
  let cancelSource: CancelTokenSource | undefined

  return async (_: unknown, v: unknown) => {
    if (!!cancelSource) {
      cancelSource.cancel()
    }

    if (!v) {
      return
    }

    cancelSource = axios.CancelToken.source()
    if (typeof v !== 'string') {
      throw 'invalid value'
    } else if (
      either.isRight(
        await pipe(checker(v, cancelSource.token), task.delay(500))()
      )
    ) {
      throw 'value exists'
    }
  }
}

const checkPhoneExists = (organization: string) =>
  checkExists((phoneNumber: string, token: CancelToken) =>
    getPayerByEmailOrPhonenumber(organization, undefined, phoneNumber, token)
  )

const checkEmailExists = (organization: string) =>
  checkExists((email: string, token: CancelToken) =>
    getPayerByEmailOrPhonenumber(organization, email, undefined, token)
  )

const AddPayerModal = () => {
  const organization = useOrganization()
  const visible = useSelector(s => s.payers.createModalOpen)
  const loading = useSelector(s => loadable.isLoading(s.payers.addResult))
  const dispatch = useDispatch()
  const [form] = Form.useForm()

  const name = option.fromNullable(organization?.name)

  React.useEffect(() => {
    if (visible) {
      form.resetFields()
    }
  }, [visible])

  return (
    <Modal
      zIndex={2}
      visible={visible}
      cancelText={<FormattedMessage id="cancel" />}
      okText={<FormattedMessage id="confirm" />}
      onCancel={() => dispatch(toggleCreatePayerModal())}
      onOk={form.submit}
      okButtonProps={{ loading }}
      title={<FormattedMessage id="newCustomer" />}
      destroyOnClose={true}
    >
      <PayerForm
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 18 }}
        form={form}
        onFinish={c => dispatch(addPayerAction(c))}
        extraRules={{
          email: [
            {
              message: <FormattedMessage id="emailExists" />,
              validator: pipe(
                name,
                option.map(checkEmailExists),
                option.getOrElse(
                  () => (_v: unknown, _v2: unknown) => Promise.reject()
                )
              ),
            },
          ],
          phoneNumber: [
            {
              message: <FormattedMessage id="phonenumberExists" />,
              validator: pipe(
                name,
                option.map(checkPhoneExists),
                option.getOrElse(
                  () => (_v: unknown, _v2: unknown) => Promise.reject()
                )
              ),
            },
          ],
        }}
      />
    </Modal>
  )
}

export default AddPayerModal
